import { cn } from "./cn";

type Shape = "bar" | "circle" | "text";

const SHAPE: Record<Shape, string> = {
  bar: "h-4 w-full rounded-md",
  circle: "h-9 w-9 rounded-full",
  text: "h-3 rounded",
};

// Base placeholder box. Compose it directly for one-off shapes, or reach for
// the higher-level helpers below (SkeletonText / SkeletonList /
// SkeletonPageHeader) so loading states stay visually consistent.
export function Skeleton({
  shape = "bar",
  class: extraClass,
}: {
  shape?: Shape;
  class?: string;
}) {
  return (
    <div
      aria-hidden
      class={cn("animate-pulse bg-bg-inset", SHAPE[shape], extraClass)}
    />
  );
}

// Multi-line paragraph placeholder. The last line is shortened so the block
// reads like prose instead of a solid rectangle.
export function SkeletonText({
  lines = 3,
  class: extraClass,
}: {
  lines?: number;
  class?: string;
}) {
  return (
    <div aria-hidden class={cn("space-y-2.5", extraClass)}>
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton
          key={i}
          shape="text"
          class={i === lines - 1 ? "w-3/5" : "w-full"}
        />
      ))}
    </div>
  );
}

// Vertical list of identical row placeholders matching the
// `<ul class="grid gap-2">` shape used by the content / keys / members lists.
// Renders the `aria-busy` wrapper so callers don't repeat it.
export function SkeletonList({
  count = 3,
  rowClass = "h-16",
  class: extraClass,
}: {
  count?: number;
  rowClass?: string;
  class?: string;
}) {
  return (
    <ul class={cn("grid gap-2", extraClass)} aria-busy="true">
      {Array.from({ length: count }, (_, i) => (
        <li key={i}>
          <Skeleton class={rowClass} />
        </li>
      ))}
    </ul>
  );
}

// Title (+ optional action buttons) placeholder mirroring PageHeader's layout
// so a screen's heading doesn't pop in once its data arrives. `actions` is the
// number of button placeholders to show on the right (matches Button md: h-9).
export function SkeletonPageHeader({ actions = 0 }: { actions?: number }) {
  return (
    <header
      aria-busy="true"
      class="flex flex-wrap items-start justify-between gap-3"
    >
      <div class="min-w-0 space-y-2">
        <Skeleton class="h-8 w-48 rounded-md" />
        <Skeleton class="h-4 w-32 rounded" />
      </div>
      {actions > 0 && (
        <div class="flex flex-wrap items-center gap-2">
          {Array.from({ length: actions }, (_, i) => (
            <Skeleton key={i} class="h-9 w-24 rounded-md" />
          ))}
        </div>
      )}
    </header>
  );
}
